import React from 'react';

import { cn } from 'domain/core';
import { ButtonProps } from './common-types';
import { useA11y } from 'domain/hooks';
import { commonInputClassName } from './Common';

type CustomButtonProps = {
	outlined?: boolean;
	size?: 'sm' | 'md' | 'lg';
};

type AppButtonProps = ButtonProps & CustomButtonProps;

export const Button = React.forwardRef((props: AppButtonProps, ref: any) => {
	const { className, outlined = false, size = 'md', ...otherProps } = props;

	const [title, ariaLabel] = useA11y(otherProps);

	return (
		<button
			{...otherProps}
			ref={ref}
			title={title}
			aria-label={ariaLabel}
			className={cn(
				'app-button',
				'flex flex-row flex-nowrap items-center justify-center',
				'rounded',
				'bg-primary-600 text-neutral-100',
				'hover:bg-primary-900 hover:text-white',
				'overflow-ellipsis whitespace-nowrap',
				'disabled:cursor-not-allowed disabled:text-gray-300',
				'transition-colors ease-in-out',
				size === 'sm' && 'py-1 px-4 text-sm',
				size === 'md' && commonInputClassName,
				size === 'lg' && 'py-2 px-6 text-lg',
				className
			)}>
			{props.children}
		</button>
	);
});

export const ButtonPrimary = React.forwardRef(
	(props: AppButtonProps, ref: any) => {
		const { className, outlined = false, ...otherProps } = props;

		return (
			<Button
				{...otherProps}
				ref={ref}
				className={
					!outlined
						? cn(
								'bg-primary-700 text-gray-200',
								'hover:bg-primary-800 hover:text-white',
								'active:bg-primary-900',
								'disabled:bg-primary-500',
								className
						  )
						: cn(
								'bg-primary-100 text-primary-700',
								'hover:bg-primary-200',
								'active:bg-primary-300 active:text-primary-900',
								'disabled:bg-primary-100 disabled:text-primary-400',
								className
						  )
				}
			/>
		);
	}
);

export const ButtonSecondary = React.forwardRef(
	(props: AppButtonProps, ref: any) => {
		const { className, outlined = false, ...otherProps } = props;

		return (
			<Button
				{...otherProps}
				ref={ref}
				className={
					!outlined
						? cn(
								'bg-secondary-700 text-gray-200',
								'hover:bg-secondary-800 hover:text-white',
								'active:bg-secondary-900',
								'disabled:bg-secondary-500 disabled:text-gray-600',
								className
						  )
						: cn(
								'bg-secondary-100 text-secondary-700 ',
								'hover:bg-secondary-200',
								'active:bg-secondary-300 active:text-secondary-900',
								'disabled:bg-secondary-100 disabled:text-secondary-400',
								className
						  )
				}
			/>
		);
	}
);

export const ButtonSuccess = React.forwardRef(
	(props: AppButtonProps, ref: any) => {
		const { className, outlined = false, ...otherProps } = props;

		return (
			<Button
				{...otherProps}
				ref={ref}
				className={
					!outlined
						? cn(
								'bg-green-700 text-gray-200',
								'hover:bg-green-800 hover:text-white',
								'active:bg-green-900',
								'disabled:bg-green-200 disabled:text-gray-600',
								className
						  )
						: cn(
								'bg-green-100 text-green-700',
								'hover:bg-green-200',
								'active:bg-green-300 active:text-green-900',
								'disabled:bg-green-100 disabled:text-green-400',
								className
						  )
				}
			/>
		);
	}
);

export const ButtonDanger = React.forwardRef(
	(props: AppButtonProps, ref: any) => {
		const { className, outlined = false, ...otherProps } = props;

		return (
			<Button
				{...otherProps}
				ref={ref}
				className={
					!outlined
						? cn(
								'bg-red-700 text-gray-200',
								'hover:bg-red-800 hover:text-white',
								'active:bg-red-900 ',
								'disabled:bg-red-300 disabled:text-gray-500',
								className
						  )
						: cn(
								'bg-red-100 text-red-700',
								'hover:bg-red-200',
								'active:bg-red-300 active:text-red-900',
								'disabled:bg-red-100 disabled:text-red-400',
								className
						  )
				}
			/>
		);
	}
);

Button.displayName = 'Button';
ButtonPrimary.displayName = 'ButtonPrimary';
ButtonSecondary.displayName = 'ButtonSecondary';
ButtonSuccess.displayName = 'ButtonSuccess';
ButtonDanger.displayName = 'ButtonDanger';
